import { MenuItemType } from "../../../../types/baseTypes";
import styles from "./SubMenu.module.scss";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import MenuItem from "../MenuItem/MenuItem";
import { MenuParentType } from "../Menu";
import { isMenuOrChildMenuSelected } from "../utils";
import { FocusTrap } from "@aleksundshantu/aunds-utils-react/accessibility";
import React, { useEffect, useRef, useState } from "react";
import { useKeyboardNavigation } from "../keyboard-navigation";
import { Post } from "@aleksundshantu/aunds-hofreiter2026-types/client";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG.client";

interface SubMenuItemProps {
  item: MenuItemType;
  pageData?: Post;
  currentSubmenu: MenuParentType;
  parentSubMenu: MenuParentType;
  subMenu: MenuParentType;
  parentMenuItem: MenuItemType;
  setCurrentSubmenu: (value: MenuParentType) => void;
  controlParentMenu: (action: "close" | "prev" | "next") => void;
}

const SubMenu = ({
  item,
  pageData,
  currentSubmenu,
  setCurrentSubmenu,
  parentSubMenu,
  subMenu,
  parentMenuItem,
  controlParentMenu,
}: SubMenuItemProps) => {
  const { getIcon, label } = useGlobalContext();
  const { items, keyDown, controlMenu, firstItemFocused } =
    useKeyboardNavigation({
      menu: item,
      setCurrentSubmenu,
      type: "submenu",
      controlParentMenu,
      subMenu,
    });
  const ref = useRef<HTMLUListElement | null>(null);
  const [outOfViewport, setOutOfViewport] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        // true when element not fully inside
        setOutOfViewport(entry.intersectionRatio < 1);
      },
      {
        threshold: 0,
      },
    );

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, []);

  return (
    <FocusTrap
      innerRef={ref}
      dep={currentSubmenu}
      disabled={currentSubmenu?.ID !== item.ID}
    >
      <ul
        className={`${styles["navbar-submenu"]} ${outOfViewport ? styles["out-of-viewport"] : ""} navbar-submenu ${currentSubmenu?.ID !== item.ID ? "trap enabled" : "trap disabled"} ${isMenuOrChildMenuSelected(item, currentSubmenu) ? styles["is-open"] : ""}`}
        role="menu"
        onKeyDown={keyDown}
      >
        <li
          className={`${styles["navbar-submenu-title"]} navbar-link is-hidden-after-navbar-breakpoint`}
        >
          {parentMenuItem.title}
        </li>
        {Array.isArray(item.child_items) &&
          item.child_items.map((child, key) => {
            if (!items.current[key]) {
              items.current[key] = React.createRef<HTMLLIElement>();
            }
            return (
              <MenuItem
                item={child}
                key={key}
                pageData={pageData}
                currentSubmenu={currentSubmenu}
                setCurrentSubmenu={setCurrentSubmenu}
                parentSubMenu={item}
                subMenu={item}
                tabIndex={-1}
                itemRef={items.current[key]}
                controlParentMenu={controlMenu}
                onFocus={key == 0 ? firstItemFocused : undefined}
              />
            );
          })}
        <li className="is-hidden-after-navbar-breakpoint" role="none">
          <button
            className="back button-reset is-flex is-align-items-center is-gap-2 navbar-sublink"
            role="menuitem"
            onClick={() => {
              setCurrentSubmenu(parentSubMenu);
            }}
          >
            <CustomSVG
              width={16}
              height={16}
              src={getIcon("arrowLeft")}
              wrapper={"span"}
              aria-hidden="true"
              className="is-flex"
            />
            <span>
              {label("global", null, "back", { en: "back", de: "zurück" })}
            </span>
          </button>
        </li>
      </ul>
    </FocusTrap>
  );
};

export default SubMenu;
